import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { FiChevronRight, FiArrowRight, FiAward, FiTrendingUp, FiUsers, FiClock } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

// Mock data for all case studies
const allCaseStudies = [
    {
        id: 1,
        title: "E-Commerce Platform Redesign",
        client: "FashionHub",
        year: "2023",
        category: "Web Development",
        excerpt: "A complete redesign that increased mobile conversions by 220% and reduced cart abandonment by 62%.",
        thumbnail: "https://images.unsplash.com/photo-1563014959-7aaa83350992?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        tags: ["E-commerce", "UX Design", "Mobile Optimization"],
        stats: [
            { label: "Conversion Rate", value: "+150%", icon: <FiTrendingUp /> },
            { label: "Mobile Revenue", value: "+220%", icon: <FiTrendingUp /> },
        ],
    },
    {
        id: 2,
        title: "Healthcare App Development",
        client: "MediCare+",
        year: "2023",
        category: "Mobile App",
        excerpt: "A patient portal app that improved appointment scheduling by 75% and reduced no-shows by 40%.",
        thumbnail: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        tags: ["Healthcare", "iOS", "Android"],
        stats: [
            { label: "User Adoption", value: "85%", icon: <FiUsers /> },
            { label: "Rating", value: "4.9/5", icon: <FiAward /> },
        ],
    },
    {
        id: 3,
        title: "SaaS Dashboard Redesign",
        client: "DataMetrics",
        year: "2022",
        category: "UI/UX Design",
        excerpt: "Redesigned analytics dashboard that improved user engagement by 300% and reduced support tickets.",
        thumbnail: "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        tags: ["SaaS", "Data Visualization", "B2B"],
        stats: [
            { label: "Engagement", value: "+300%", icon: <FiTrendingUp /> },
            { label: "Retention", value: "+45%", icon: <FiUsers /> },
        ],
    },
    {
        id: 4,
        title: "Restaurant Loyalty Program",
        client: "UrbanEats",
        year: "2022",
        category: "Mobile App",
        excerpt: "Custom loyalty program that increased repeat customers by 65% and average order value by 28%.",
        thumbnail: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        tags: ["Hospitality", "Loyalty", "POS Integration"],
        stats: [
            { label: "Repeat Customers", value: "+65%", icon: <FiUsers /> },
            { label: "Order Value", value: "+28%", icon: <FiTrendingUp /> },
        ],
    },
    {
        id: 5,
        title: "Fintech Mobile Banking",
        client: "NeoBank",
        year: "2021",
        category: "Mobile App",
        excerpt: "Mobile banking app that acquired 250,000 users in the first 6 months with 4.8 star rating.",
        thumbnail: "https://images.unsplash.com/photo-1563014959-7aaa83350992?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        tags: ["Fintech", "Banking", "Security"],
        stats: [
            { label: "New Users", value: "250K", icon: <FiUsers /> },
            { label: "Rating", value: "4.8/5", icon: <FiAward /> },
        ],
    },
    {
        id: 6,
        title: "Corporate Website Redesign",
        client: "GlobalTech",
        year: "2021",
        category: "Web Development",
        excerpt: "Enterprise website redesign that improved lead generation by 180% and SEO traffic by 150%.",
        thumbnail: "https://images.unsplash.com/photo-1497215728101-856f4ea42174?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
        tags: ["Corporate", "SEO", "Lead Generation"],
        stats: [
            { label: "Leads", value: "+180%", icon: <FiTrendingUp /> },
            { label: "SEO Traffic", value: "+150%", icon: <FiTrendingUp /> },
        ],
    },
];

const CaseStudiesPage = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [activeCategory, setActiveCategory] = useState('All');
    const [viewMode, setViewMode] = useState('grid');

    // Handle scroll for header effect
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Get unique categories
    const categories = ['All', ...new Set(allCaseStudies.map(study => study.category))];

    // Filter by category if needed
    const filteredCaseStudies = activeCategory === 'All'
        ? allCaseStudies
        : allCaseStudies.filter(study => study.category === activeCategory);

    return (
        <div>
            <Helmet>
                <title>Case Studies | Our Portfolio of Digital Success Stories</title>
                <meta name="description" content="Explore our collection of case studies showcasing innovative digital solutions that delivered measurable results for our clients." />
            </Helmet>

            {/* Hero Section with Parallax Effect */}
            <section className="relative h-screen  flex items-center justify-center overflow-hidden pt-10">
                {/* Animated background elements */}
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 via-purple-900 to-blue-900 overflow-hidden">
                    {[...Array(20)].map((_, i) => (
                        <motion.div
                            key={i}
                            className="absolute rounded-full bg-white/10 backdrop-blur-sm"
                            animate={{
                                y: [0, Math.random() * 100 - 50],
                                x: [0, Math.random() * 100 - 50],
                                scale: [0, 1, 0],
                                opacity: [0, 0.3, 0]
                            }}
                            transition={{
                                duration: 5 + Math.random() * 10,
                                repeat: Infinity,
                                delay: Math.random() * 5,
                                ease: "easeInOut"
                            }}
                            style={{
                                width: `${Math.random() * 200 + 50}px`,
                                height: `${Math.random() * 200 + 50}px`,
                                top: `${Math.random() * 100}%`,
                                left: `${Math.random() * 100}%`,
                            }}
                        />
                    ))}
                </div>

                <div className="md:container mx-auto px-6 relative z-10 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        <motion.span
                            className="inline-block bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full text-sm font-medium mb-6 text-white border border-white/20"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5, delay: 0.4 }}
                        >
                            Proven Success Stories
                        </motion.span>

                        <motion.h1
                            className="text-5xl md:text-7xl font-bold mb-6 leading-tight text-white"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                        >
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-purple-400">
                                Transformative
                            </span>{' '}
                            <br />
                            Digital Excellence
                        </motion.h1>

                        <motion.p
                            className="text-xl md:text-2xl opacity-90 mb-10 max-w-3xl mx-auto text-white/90"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.8, delay: 0.8 }}
                        >
                            Discover how we've helped industry leaders solve complex challenges through innovative digital experiences that deliver measurable results.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 1 }}
                            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
                        >
                            <motion.a
                                href="#case-studies"
                                className="inline-flex items-center bg-white text-indigo-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all shadow-lg hover:shadow-cyan-500/20"
                                whileHover={{ scale: 1.05, boxShadow: "0 10px 25px -5px rgba(59, 130, 246, 0.4)" }}
                                whileTap={{ scale: 0.95 }}
                            >
                                Explore Our Case Studies
                                <FiArrowRight className="ml-2" />
                            </motion.a>

                            <motion.a
                                href="#contact"
                                className="inline-flex items-center border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white/10 transition-all"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                Contact Us
                            </motion.a>
                        </motion.div>
                    </motion.div>
                </div>


            </section>

            {/* Stats Section */}
            <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-700 text-white">
                <div className="md:container mx-auto px-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            viewport={{ once: true }}
                        >
                            <div className="text-4xl md:text-5xl font-bold mb-2">30+</div>
                            <div className="text-sm uppercase tracking-wider">Projects Completed</div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            viewport={{ once: true }}
                        >
                            <div className="text-4xl md:text-5xl font-bold mb-2">98%</div>
                            <div className="text-sm uppercase tracking-wider">Client Satisfaction</div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            viewport={{ once: true }}
                        >
                            <div className="text-4xl md:text-5xl font-bold mb-2">12</div>
                            <div className="text-sm uppercase tracking-wider">Industries Served</div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                            viewport={{ once: true }}
                        >
                            <div className="text-4xl md:text-5xl font-bold mb-2">3+</div>
                            <div className="text-sm uppercase tracking-wider">Years Experience</div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Case Studies Section */}
            <section id="case-studies" className="py-20 bg-gray-50">
                <div className="md:container mx-auto px-6">
                    <motion.div
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <motion.span
                            className="inline-block mb-4 text-sm font-semibold tracking-wider text-indigo-600 uppercase"
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            viewport={{ once: true }}
                        >
                            Our Portfolio
                        </motion.span>

                        <motion.h2
                            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            viewport={{ once: true }}
                        >
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
                                Success Stories
                            </span>{' '}
                            That Drive Growth
                        </motion.h2>

                        <motion.p
                            className="text-xl text-gray-600 max-w-3xl mx-auto"
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                            viewport={{ once: true }}
                        >
                            Explore our collection of transformative case studies that showcase our expertise in delivering exceptional digital solutions.
                        </motion.p>
                    </motion.div>



                    {/* Case Studies Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <AnimatePresence>
                            {filteredCaseStudies.map((study, index) => (
                                <motion.div
                                    key={study.id}
                                    className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 hover:shadow-2xl transition-all h-full flex flex-col"
                                    initial={{ opacity: 0, y: 50 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{
                                        duration: 0.5,
                                        delay: index * 0.1,
                                        type: "spring",
                                        stiffness: 100
                                    }}
                                    viewport={{ once: true }}
                                    whileHover={{
                                        y: -10,
                                    }}
                                >
                                    <div className="relative h-64 overflow-hidden group">
                                        <motion.img
                                            src={study.thumbnail}
                                            alt={`${study.title} case study thumbnail`}
                                            className="w-full h-full object-cover"
                                            whileHover={{ scale: 1.1 }}
                                            transition={{ duration: 0.5 }}
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent flex flex-col justify-between p-6">
                                            <div>
                                                <motion.span
                                                    className="inline-block bg-white text-indigo-800 px-3 py-1 rounded-full text-xs font-medium mb-2"
                                                    whileHover={{ scale: 1.05 }}
                                                >
                                                    {study.category}
                                                </motion.span>
                                            </div>
                                            <div>
                                                <motion.h3
                                                    className="text-2xl font-bold text-white"
                                                    whileHover={{ x: 5 }}
                                                >
                                                    {study.title}
                                                </motion.h3>
                                                <p className="text-white/90 text-sm">{study.client} • {study.year}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="p-6 flex-grow">
                                        <p className="text-gray-600 mb-4">{study.excerpt}</p>

                                        <div className="flex flex-wrap gap-2 mb-6">
                                            {study.tags.map(tag => (
                                                <motion.span
                                                    key={tag}
                                                    className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-xs"
                                                    whileHover={{ scale: 1.05 }}
                                                >
                                                    {tag}
                                                </motion.span>
                                            ))}
                                        </div>

                                        <div className="grid grid-cols-2 gap-3 mb-6">
                                            {study.stats.map((stat, i) => (
                                                <motion.div
                                                    key={i}
                                                    className="bg-indigo-50 p-3 rounded-lg flex items-center"
                                                    whileHover={{ scale: 1.03 }}
                                                >
                                                    <div className="text-indigo-600 mr-2">{stat.icon}</div>
                                                    <div>
                                                        <p className="text-lg font-bold text-indigo-700">{stat.value}</p>
                                                        <p className="text-xs text-gray-600">{stat.label}</p>
                                                    </div>
                                                </motion.div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="px-6 pb-6">
                                        <motion.a
                                            href={`/case-studies/${study.id}`}
                                            className="inline-flex items-center justify-between w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:bg-indigo-700 px-6 py-3 rounded-lg font-medium transition-colors group"
                                            aria-label={`Read more about ${study.title} case study`}
                                            whileHover={{
                                                scale: 1.02,
                                                background: "linear-gradient(to right, #4f46e5, #7c3aed)"
                                            }}
                                        >
                                            <span>View Case Study</span>
                                            <FiChevronRight className="ml-2 transition-transform group-hover:translate-x-1" />
                                        </motion.a>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>
                </div>
            </section>

            {/* Testimonial Section */}
            <section className="py-20 bg-gradient-to-r from-indigo-50 to-purple-50">
                <div className="md:container mx-auto px-6">
                    <motion.div
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <motion.h2
                            className="text-4xl md:text-5xl font-bold mb-6"
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            viewport={{ once: true }}
                        >
                            What Our <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">Clients Say</span>
                        </motion.h2>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <motion.div
                            className="bg-white p-8 rounded-2xl shadow-lg"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            viewport={{ once: true }}
                            whileHover={{ y: -5 }}
                        >
                            <div className="text-yellow-400 text-2xl mb-4">★★★★★</div>
                            <p className="text-gray-600 mb-6">"Their team transformed our e-commerce platform, resulting in a 220% increase in mobile conversions. The attention to detail and strategic approach exceeded our expectations."</p>
                            <div className="flex items-center">
                                <div className="w-12 h-12 bg-indigo-600 rounded-full mr-4"></div>
                                <div>
                                    <div className="font-bold">Sarah Johnson</div>
                                    <div className="text-sm text-gray-500">CEO, FashionHub</div>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            className="bg-white p-8 rounded-2xl shadow-lg"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            viewport={{ once: true }}
                            whileHover={{ y: -5 }}
                        >
                            <div className="text-yellow-400 text-2xl mb-4">★★★★★</div>
                            <p className="text-gray-600 mb-6">"The healthcare app they developed revolutionized our patient experience. Appointment no-shows decreased by 40% and patient satisfaction scores reached an all-time high."</p>
                            <div className="flex items-center">
                                <div className="w-12 h-12 bg-purple-600 rounded-full mr-4"></div>
                                <div>
                                    <div className="font-bold">Dr. Michael Chen</div>
                                    <div className="text-sm text-gray-500">Medical Director, MediCare+</div>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            className="bg-white p-8 rounded-2xl shadow-lg"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                            viewport={{ once: true }}
                            whileHover={{ y: -5 }}
                        >
                            <div className="text-yellow-400 text-2xl mb-4">★★★★★</div>
                            <p className="text-gray-600 mb-6">"Working with them was a game-changer for our SaaS platform. The redesigned dashboard led to a 300% increase in user engagement and significantly reduced our support costs."</p>
                            <div className="flex items-center">
                                <div className="w-12 h-12 bg-blue-600 rounded-full mr-4"></div>
                                <div>
                                    <div className="font-bold">Alex Rodriguez</div>
                                    <div className="text-sm text-gray-500">Product Lead, DataMetrics</div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section id="contact" className="py-20 bg-gray-900 text-white">
                <div className="md:container mx-auto px-6 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="max-w-4xl mx-auto"
                    >
                        <motion.span
                            className="inline-block mb-4 text-sm font-semibold tracking-wider text-cyan-400 uppercase"
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            viewport={{ once: true }}
                        >
                            Get Started
                        </motion.span>

                        <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to create your success story?</h2>

                        <p className="text-xl opacity-90 mb-10 max-w-2xl mx-auto">
                            Let's collaborate to build something extraordinary. Our team is ready to bring your vision to life.
                        </p>

                        <div className="flex flex-wrap justify-center gap-4">
                            <motion.a
                                href="/contact"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="inline-flex items-center bg-cyan-500 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-cyan-600 transition-all shadow-lg hover:shadow-cyan-500/30"
                            >
                                Get Started
                                <FiArrowRight className="ml-2" />
                            </motion.a>

                            <motion.a
                                href="/services"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="inline-flex items-center border-2 border-gray-700 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white/10 transition-all"
                            >
                                Our Services
                            </motion.a>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default CaseStudiesPage;